import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlphabetSelector,
  Box,
  ContentPanel,
  Divider,
  OnDesktop,
  OnMobile,
  Svg,
  Tab,
  Tabs,
  useMediaQuery,
} from '@hopskipdrive/react-components';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import * as Constants from '@rideiq/constants';
import { useFlags } from '@rideiq/contexts/LDContext';
import { optionsToString, stringToOptions } from '@rideiq/reports/util/queryToFilter';
import { RidersSelectionBanner } from '@rideiq/riders/features/RidersTable/RidersSelectionBanner';
import { useRidersBulkSelection } from '@rideiq/riders/features/RidersTable/useRidersBulkSelection';
import { ActionBar, SearchNavBar } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { FilterDrawer } from '@rideiq/shared/components/FilterDrawer';
import { PageErrorFallback } from '@rideiq/shared/components/PageErrorFallback';
import { colorPalette, variables } from '@rideiq/shared/components/variables';
import { ErrorBoundary } from '@rideiq/utils/errorReporting/ErrorBoundary';

import { analyticsPage } from '../../../api/services/Analytics';
import { getRidersAvailableAlpha } from '../../../api/services/Riders';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { getDownloadFile } from '../../../utils/helpers/getDownloadFile';
import { hideZendeskWidget, showZendeskWidget } from '../../../vendor/features/Chat/Chat.service';
import CreateRider from '../../features/CreateRider';
import { InvoiceGroupOption, InvoiceGroupsFilter } from '../../features/InvoiceGroupsFilter';
import { RidersTable } from '../../features/RidersTable';
import { UploadCsvFile } from '../../features/UploadCsvFile';
import { UploadsTable } from '../../features/UploadsTable';
import { useRiderFilters } from '../../hooks/useRiderFilters';
import { useRidersQuery } from '../../hooks/useRidersQuery';
import { useRidersUrlSync } from '../../hooks/useRidersUrlSync';

import { AllLink, PageFilters } from './styles';

const ALPHABET_HEIGHT = 53;
const ALPHABET_MOBILE_HEIGHT = 68;

const Riders: React.FC = () => {
  const {
    riders,
    isLoading,
    isError,
    fetchRiders,
    debounceHandleQuery,
    handleQuery,
    queryParams,
    bulkActionQueryParams,
    totalCount,
    handlePageChange,
    handleSort,
    clearSort,
    clearAllFilters: clearAllRidersFilters,
    setActiveFilters,
  } = useRidersQuery();
  const { navigateTo } = useNavigation();
  const [searchParams] = useSearchParams();
  const [riderAvailableLetters, setRiderAvailableLetters] = useState<RiderAvailableAlpha[]>([]);
  const initialTab = (searchParams.get('state') as 'active' | 'deactivated' | 'paused' | 'uploads') || 'active';
  const initialInvoiceGroup = searchParams.get('invoiceGroups');
  const [currentStatusTab, setCurrentStatusTab] = useState<'active' | 'deactivated' | 'paused' | 'uploads'>(initialTab);
  const { isImpersonating, isAdminOrOrganizerUser } = useAuthQuery();
  const isMobileOrTablet = useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 });
  const alphabetHeight = isMobileOrTablet ? ALPHABET_MOBILE_HEIGHT : ALPHABET_HEIGHT;
  const tableHeightOffset = variables.headingHeights.withTitleTabsFilters + (!queryParams.query ? alphabetHeight : 0);
  const [isFetchingFileToDownload, setIsFetchingFileToDownload] = useState(false);
  const { enableRidersAdvancedSorting } = useFlags();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const { sections, activeFilterCount, hasActiveFilters, clearAllFilters } = useRiderFilters(filterDrawerOpen, {
    queryParams,
    setActiveFilters,
    clearAllFilters: clearAllRidersFilters,
  });

  const hasSortActive = !!queryParams.sortBy;
  const handleClearAll = () => {
    clearAllFilters();
    clearSort();
  };

  const openFilterDrawer = () => {
    hideZendeskWidget();
    setFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => {
    showZendeskWidget();
    setFilterDrawerOpen(false);
  };
  const [selectedInvoiceGroups, setSelectedInvoiceGroups] = useState<InvoiceGroupOption[]>(
    initialInvoiceGroup ? stringToOptions(initialInvoiceGroup) : []
  );
  const {
    selectedRiderIds,
    handleCheckboxSelection,
    handleSelectAllOnPage,
    handleSelectAllFiltered,
    clearSelection,
    isAllFilteredSelected,
    selectedCount,
  } = useRidersBulkSelection();

  const handleRiderClick = ({ slug }) => {
    navigateTo(`/riders/${slug}`, { query: searchParams.toString() });
  };

  const handleSelectedLetter = (selectedLetter) => {
    debounceHandleQuery(selectedLetter, { state: currentStatusTab }, Boolean(selectedLetter));
  };

  const handleTabClick = (currentTab) => {
    currentTab !== 'uploads' &&
      debounceHandleQuery(
        queryParams.query || '',
        { state: currentTab },
        queryParams.byRiderSearchAlpha || queryParams.query?.length === 1
      );
    setCurrentStatusTab(currentTab);
  };

  const handleAllClick = () => {
    setRiderAvailableLetters([]);
    debounceHandleQuery('', { state: currentStatusTab }, false);
  };

  const handleDownloadTemplateFile = async () => {
    setIsFetchingFileToDownload(true);
    await getDownloadFile('template', 'rider');
    setIsFetchingFileToDownload(false);
  };

  useRidersUrlSync({
    queryParams,
    handleQuery,
    setActiveFilters,
    enableRidersAdvancedSorting,
    selectedInvoiceGroups,
    onStatusChange: setCurrentStatusTab,
  });

  useEffect(() => {
    const abortController = new AbortController();
    const fetchRidersAvailableLetters = async () => {
      const response = await getRidersAvailableAlpha(abortController.signal);

      if (response?.type === 'success' && response.data.searchableChars?.length > 0) {
        setRiderAvailableLetters(response.data.searchableChars);
      }
    };

    !riderAvailableLetters.length && fetchRidersAvailableLetters();

    return () => abortController.abort();
  }, [riderAvailableLetters.length]);

  useEffect(() => {
    analyticsPage('Riders');
  }, []);

  useEffect(() => {
    // Prevents a stale cross-tab selection (e.g. from the Active tab) from leaking into bulk actions on this tab.
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatusTab]);

  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback}>
      <ActionBar>
        <ActionBar.Title title="Riders" />
        <ActionBar.SideControls>
          <Can I={actions.add} a={subjects.rider}>
            <CreateRider />
          </Can>
        </ActionBar.SideControls>
      </ActionBar>
      <Divider bgColor={colorPalette.neutralVariant90} opacity={0.5} />
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" rowGap={2} margin="24px 0 24px 0">
        <Tabs
          value={currentStatusTab}
          showIndicator
          aria-label="Rider status"
          variant="scrollable"
          sx={{ maxWidth: '100%', flexShrink: 0 }}
        >
          <Tab label="Active" value="active" onClick={() => handleTabClick('active')} />
          <Tab label="Paused" value="paused" onClick={() => handleTabClick('paused')} />
          <Tab label="Deactivated" value="deactivated" onClick={() => handleTabClick('deactivated')} />
          {isImpersonating && <Tab label="Batch Uploads" value="uploads" onClick={() => handleTabClick('uploads')} />}
        </Tabs>
        {currentStatusTab === 'uploads' ? (
          <Box>
            <Button
              color="secondary"
              size="medium"
              style={{ marginRight: '28px' }}
              startIcon={<Svg icon="download" width="16px" />}
              onClick={() => handleDownloadTemplateFile()}
              disabled={isFetchingFileToDownload}
            >
              Download Template file
            </Button>
            <UploadCsvFile />
          </Box>
        ) : null}
      </Box>
      {currentStatusTab !== 'uploads' && (
        <PageFilters>
          {!enableRidersAdvancedSorting && (
            <FilterAltRoundedIcon style={{ color: colorPalette.neutral350, flexShrink: 0 }} />
          )}
          {isAdminOrOrganizerUser && !enableRidersAdvancedSorting && (
            <InvoiceGroupsFilter
              value={selectedInvoiceGroups}
              onChange={(value) => {
                setSelectedInvoiceGroups(value);
                debounceHandleQuery(queryParams.query || '', {
                  invoiceGroups: optionsToString(value),
                  state: queryParams.state,
                });
              }}
            />
          )}
          <SearchNavBar
            id="search-field"
            debounceHandleQuery={debounceHandleQuery}
            placeholder="Search all riders"
            accessibleLabel="Search all riders"
            query={queryParams.query}
          />
          {enableRidersAdvancedSorting && (
            <Box position="relative" display="inline-flex" alignItems="center">
              <Button color="secondary" onClick={openFilterDrawer} size="medium">
                Filters
              </Button>
              {activeFilterCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-10px',
                    bgcolor: colorPalette.brandNavy,
                    color: colorPalette.neutral100,
                    borderRadius: '10px',
                    minWidth: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    px: '4px',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          )}
          {enableRidersAdvancedSorting && (hasActiveFilters || hasSortActive) && (
            <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={handleClearAll}>
              <Box component="span" sx={{ color: colorPalette.neutral10, fontSize: 13 }}>
                Clear all
              </Box>
            </Box>
          )}
        </PageFilters>
      )}
      {enableRidersAdvancedSorting && (
        <FilterDrawer
          animateSections
          open={filterDrawerOpen}
          onClose={closeFilterDrawer}
          sections={sections}
          onApply={setActiveFilters}
          onClearAll={clearAllFilters}
        />
      )}
      {selectedCount > 0 && (
        <RidersSelectionBanner
          selectedRiderIds={selectedRiderIds}
          pageRiderCount={riders.length}
          totalCount={totalCount}
          isAllFilteredSelected={isAllFilteredSelected}
          onSelectAllFiltered={handleSelectAllFiltered}
          onClearSelection={() => {
            clearSelection();
            handlePageChange({ page: 1, perPage: Constants.DEFAULT_ROWS_PER_PAGE }, true);
          }}
          onMoveSuccess={() => {
            setSelectedInvoiceGroups([]);
            handleQuery(queryParams.query || '', { state: queryParams.state, invoiceGroups: null });
          }}
          queryParams={bulkActionQueryParams}
        />
      )}
      <OnMobile>
        {queryParams.query && queryParams.query.length > 1 && !queryParams.byRiderSearchAlpha ? (
          <> </>
        ) : (
          <AlphabetSelector
            availableLetters={riderAvailableLetters
              .filter((riderLetter) => riderLetter.status === currentStatusTab)
              .map((riderLetter) => riderLetter.anchor)}
            handleSelected={handleSelectedLetter}
          >
            <AllLink key={'all'} onClick={handleAllClick} data-testid="all-link" ml={2}>
              {'All'}
            </AllLink>
          </AlphabetSelector>
        )}
      </OnMobile>
      {currentStatusTab === 'uploads' ? (
        <UploadsTable />
      ) : (
        <ContentPanel>
          <OnDesktop>
            {queryParams.query && queryParams.query.length > 1 && !queryParams.byRiderSearchAlpha ? (
              <> </>
            ) : (
              <AlphabetSelector
                availableLetters={riderAvailableLetters
                  .filter((riderLetter) => riderLetter.status === currentStatusTab)
                  .map((riderLetter) => riderLetter.anchor)}
                handleSelected={handleSelectedLetter}
              >
                <AllLink key={'all'} onClick={handleAllClick} data-testid="all-link">
                  {'All'}
                </AllLink>
              </AlphabetSelector>
            )}
          </OnDesktop>
          <RidersTable
            currentStatusTab={currentStatusTab}
            handleRiderClick={handleRiderClick}
            tableHeightOffset={tableHeightOffset}
            selectedRiderIds={selectedRiderIds}
            handleCheckboxSelection={handleCheckboxSelection}
            clearSelection={clearSelection}
            handleSelectAllOnPage={handleSelectAllOnPage}
            riders={riders}
            isLoading={isLoading}
            isError={isError}
            queryParams={queryParams}
            handlePageChange={handlePageChange}
            handleSort={handleSort}
            clearAllFilters={clearAllRidersFilters}
            totalCount={totalCount}
            fetchRiders={fetchRiders}
          />
        </ContentPanel>
      )}
    </ErrorBoundary>
  );
};

export default Riders;
